import { createAsyncThunk } from "@reduxjs/toolkit";
import type { NavigationNode, NavigationNodeDTO } from "~/domains/navigation/types/NavigationTypes";
import type { NavigationData, NavigationProps } from "~/bridges/header/Navigation";
import { toMapFn } from "~/common/utils/reducers";
import { getNextUID } from "~/common/utils/uid";
import { resolveUrl } from "~/utils/url/urlResolver";

export interface FullNavData extends NavigationData {
  flyoutNodes: NavigationNode[];
}

export const loadNavigation = createAsyncThunk<FullNavData, NavigationProps>(
  "navigation/load",
  async (dto, { rejectWithValue }) => {
    const data = transform(dto);

    if (!data) {
      return rejectWithValue("Failed to parse navigation data");
    }

    return data;
  },
);

function transform({ navigation, flyoutNavigation }: NavigationProps): FullNavData | null {
  if (!navigation?.length) {
    return null;
  }

  try {
    const navData = transformNavigationNodes(navigation);

    let flyoutNodes: NavigationNode[] = [];
    if (flyoutNavigation?.length) {
      try {
        const data = transformNavigationNodes(flyoutNavigation);
        flyoutNodes = data.root.children.map((id) => data.nodes[id]);
        flyoutNodes.forEach((node) => (node.children = []));
      } catch (e) {
        console.error("Error when transforming flyout navigation");
        console.error(e);
      }
    }

    return { ...navData, flyoutNodes };
  } catch (e) {
    console.error("Error transforming nav data");
    console.error(e);
  }

  return null;
}

function transformNavigationNodes(dto: NavigationNodeDTO[]): NavigationData {
  const root: NavigationNode = {
    id: getNextUID(),
    gaLabel: "",
    title: "",
    level: -1,
    openInNewTab: false,
    children: [],
    usefulTools: [],
    showSeriesTree: false,
  };

  const level0Nodes = dto.map((node) => transformSingle(node, 0));
  root.children = level0Nodes.map(({ node }) => node.id);

  const nodes = toMapFn(flattenChildren(level0Nodes), (node) => node.id);

  return { root, nodes };
}

type TransformSingleResult = { node: NavigationNode; allSubNodes: NavigationNode[] };

function transformSingle(dto: NavigationNodeDTO, level: number): TransformSingleResult {
  const children = dto.children.map((node) => transformSingle(node, level + 1));

  let bigImage = undefined;
  if (dto.bigImage) {
    bigImage = {
      ...dto.bigImage,
      id: getNextUID(),
    };
  }

  let smallImage = undefined;
  if (dto.smallImage) {
    smallImage = {
      ...dto.smallImage,
      id: getNextUID(),
    };
  }

  const node: NavigationNode = {
    ...dto,
    url: dto.url ? resolveUrl(dto.url) : undefined,
    openInNewTab: dto.openInNewTab ?? false,
    showSeriesTree: dto.showSeriesTree ?? false,
    level,
    id: getNextUID(),
    bigImage,
    smallImage,
    children: children.map((child) => child.node.id),
    usefulTools: dto.usefulTools ?? [],
    pimCategoryNode: dto.pimCategoryNode ?? false,
  };

  const allSubNodes = flattenChildren(children);

  return { node, allSubNodes };
}

const flattenChildren = (children: TransformSingleResult[]) =>
  children.map(({ node, allSubNodes }) => [node, ...allSubNodes]).flat();

const STORAGE_NAVIGATION_KEY = "cache.navigation";

export function invalidateNavigationCache() {
  window.sessionStorage.removeItem(STORAGE_NAVIGATION_KEY);
  deleteCookie({ name: STORAGE_NAVIGATION_KEY });
}

interface CookieProps {
  name: string;
  value?: string;
  path?: string;
  domain?: string;
  maxAge?: string;
  expires?: string;
}

function setCookie({
  name,
  value = "",
  path = "/",
  domain = document.location.hostname,
  maxAge,
  expires,
}: CookieProps) {
  let cookie = `${name}=${value};path=${path};domain=${domain};`;
  if (maxAge) {
    cookie += `max-age=${maxAge};`;
  }
  if (expires) {
    cookie += `expires=${expires};`;
  }
  document.cookie = cookie;
}

function deleteCookie(data: Pick<CookieProps, "name" | "path" | "domain">) {
  setCookie({ ...data, expires: "Thu, 01 Jan 1970 00:00:01 GMT" });
}
